import { call, put, select } from 'redux-saga/effects'
import { setThumbnail } from '../api/builder'
import { THUMBNAILS_MIN_MAP_WIDTH } from '../ui/builderConstants'
import {
  showGlobalBuilderError,
  isAutosavingEnabledSelector,
  setTakeMapScreenshots,
  mapConfigHashSelector
} from './builderSlice'
import { Map } from 'features/maps/types'
import { localStorageSupported } from 'app/api/localStorage'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* saveMapThumbnail(): Generator<any, any, any> {
  try {
    const takeMapScreenshots: boolean = yield select((state) => state.builder.takeMapScreenshots)
    const mapScreenshots: string[] = yield select((state) => state.builder.mapScreenshots)
    const thumbnailWidth: number = yield select((state) => state.keplerGl.map?.mapState.width)

    if (takeMapScreenshots) return
    if (mapScreenshots.length === 0 || mapScreenshots.some((e) => e === undefined)) return
    if (thumbnailWidth < THUMBNAILS_MIN_MAP_WIDTH) return

    const thumbnailHeight = getSixteenByNineHeight(thumbnailWidth)
    const canvas = document.createElement('canvas')
    canvas.width = thumbnailWidth
    canvas.height = thumbnailHeight

    for (const [index, mapScreenshot] of mapScreenshots.entries()) {
      yield drawScreenshotFragment({
        canvas,
        mapScreenshot,
        thumbnailWidth,
        thumbnailHeight,
        index,
        total: mapScreenshots.length
      })
    }
    const imageData = canvas.toDataURL('image/png')
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const mapId = currentMap.id
    yield call(setThumbnail, mapId, imageData)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    console.error('saveMapThumbnail failed', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

function drawScreenshotFragment({
  canvas,
  mapScreenshot,
  thumbnailWidth,
  thumbnailHeight,
  index,
  total
}: {
  canvas: HTMLCanvasElement
  mapScreenshot: string
  thumbnailWidth: number
  thumbnailHeight: number
  index: number
  total: number
}) {
  return new Promise((resolve) => {
    const img = new Image()
    img.src = mapScreenshot
    const ctx = canvas.getContext('2d')
    img.onload = () => {
      const imageHeight = getSixteenByNineHeight(img.width)
      const centerX = total === 1 ? 0 : (img.width - thumbnailWidth) / total
      const centerY = (img.height - imageHeight) / 2
      const dx = (thumbnailWidth / total) * index
      ctx?.drawImage(img, centerX, centerY, img.width, imageHeight, dx, 0, thumbnailWidth, thumbnailHeight)
      resolve(true)
    }
  })
}

// Got from Kepler.gl https://github.com/keplergl/kepler.gl/blob/master/src/constants/src/default-settings.ts#L904
function getSixteenByNineHeight(width: number): number {
  return Math.round(width * 0.5625)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* createMapThumbnail(): Generator<any, any, any> {
  try {
    // Disable thumbnails generation for E2E tests
    if (localStorageSupported() && localStorage.getItem('E2E_LOGIN_TOKEN')) return
    const isAutosavingEnabled: boolean = yield select(isAutosavingEnabledSelector)

    if (!isAutosavingEnabled) return
    const width: number = yield select((state) => state.keplerGl.map?.mapState.width)

    if (width < THUMBNAILS_MIN_MAP_WIDTH) return
    // Disable thumbnails while an animationg is running
    const isAnimating = yield select((state) => state.keplerGl.map?.visState?.filters?.find((f) => f.isAnimating))

    if (isAnimating) return
    const mapConfigHash = yield getKeplerGlConfigHash()
    const lastThumbnailMapConfigHash = yield select((state) => state.builder.lastThumbnailMapConfigHash)
    const mapHasChanged = mapConfigHash !== lastThumbnailMapConfigHash
    // Avoid thumbnails genaration loops.
    if (!mapHasChanged) {
      return
    }
    yield put(setTakeMapScreenshots(true))

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    console.error('createMapThumbnail failed', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

function* getKeplerGlConfigHash(): Generator {
  // eslint-disable-next-line redux-saga/no-unhandled-errors
  return yield select(mapConfigHashSelector)
}
